import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  formatCallDuration,
  formatPhoneNumber,
  RECAP_MIN_UNREAD,
  type PhoneCallLog,
} from '@avslack/shared';
import { markFirstMessages } from '../lib/vitals';
import { isUnreadHeld } from '../lib/unread-hold';
import type { Message, PublicUser, TranscriptState } from '@avslack/shared';
import { dayKey, formatDay, formatTime } from '../lib/format';
import { JumpToDateDialog } from './JumpToDateDialog';
import { useMinute } from '../lib/clock';
import type { LocalMessage } from '../data/messages';
import { Avatar } from './Avatar';
import { UserLink } from './UserLink';
import { MessageRow, type MessageActions } from './MessageRow';
import { MessageStatus } from './MessageStatus';
import { useActiveHuddles } from '../data/huddles';
import { UserStatus } from './UserStatus';
import { Icon } from './Icon';
import { TaskLink, taskFromMeta } from './TaskLink';
import { ChannelRecap } from './ChannelRecap';
import { TranscriptDialog } from './TranscriptDialog';
import { useTranscriptStates } from '../data/transcripts';
import { useHuddleNotes } from '../data/huddle-notes';
import { useUsers } from '../data/users';
import { HuddlePollResultRow } from './HuddlePollResult';
import { CalendarMessageCard } from './CalendarMessageCard';
import { useDialFromChannel, useElapsed } from './PhoneLegs';
import { AssignCustomer } from './CustomerCard';
import { TtCallNote } from './TtCallNote';
import { useCustomerSystem } from '../lib/session';
import { usePhoneContacts } from '../data/phone';
import { openHuddleWindow } from '../lib/huddle-window';

/** Ennel kozelebb az aljahoz mar "alul vagyunk" - ilyenkor kovetjuk az uj uzeneteket. */
const STICK_THRESHOLD_PX = 120;
/**
 * Ennyin belul ugyanattol a szerzotol KOZOS FEJLEC ala kerul az uzenet.
 *
 * Nem az uzenetek olvadnak ossze - azok kulon buborekok maradnak -, csak a
 * profilkep, a nev es az ido nem ismetlodik. Ot perc utan mar uj gondolat
 * kezdodik, ott az ujramondas segit.
 */
const GROUP_WINDOW_MS = 5 * 60 * 1000;
/**
 * Belepeskor legfeljebb ennyi regebbi oldalt toltunk be, hogy elerjuk az elso
 * olvasatlant (oldalankent 50 uzenet). Ennel tobb olvasatlannal alul maradunk,
 * a felso "olvasatlan uzenet" gombbal.
 */
const UNREAD_ANCHOR_MAX_PAGES = 8;
/** Ennyi hely marad az "Új üzenetek" vonal folott, hogy latsszon, mire jott. */
const UNREAD_ANCHOR_OFFSET_PX = 56;
/** Ennyi ideig tekintjuk a gorgetest a felhasznalo lefele gorgetesenek. */
const SCROLL_INTENT_MS = 600;

interface MessageListProps {
  channelId: string;
  /** DM vagy csoportos DM: ott az uzenet-allapot nevvel es uzenetenkent latszik. */
  isDirect: boolean;
  /** A sajat jegyzetek beszelgetese - oda mas ures allapot szol. */
  isNotes: boolean;
  messages: LocalMessage[];
  /** Terero nelkul nyitott csatorna: az uzenetek most nem tolthetok be (K2). */
  offline?: boolean;
  usersById: Map<string, PublicUser>;
  meId: string;
  canManage: boolean;
  lastReadSeq: number;
  hasMore: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => void;
  /**
   * Van-e meg UJABB uzenet a betoltott ablak utan (permalink-ablak).
   *
   * Amig igaz, a lista alja nem a beszelgetes alja: nem tapadunk oda, es
   * lefele gorgetve tovabb toltunk.
   */
  hasMoreNewer: boolean;
  loadingNewer: boolean;
  onLoadNewer: () => void;
  /** Egy lepesben vissza a legfrissebb oldalra. */
  onJumpToLatest: () => void;
  /**
   * Szamlalo: valahanyszor no, a lista az ALJARA ugrik.
   *
   * A "legfrissebbre" ugras utan a nezet kulonben az uj oldal TETEJEN allna meg
   * - a gomb hasznalója epp az ellenkezojet kerte.
   */
  scrollToBottomToken: number;
  onAtBottomChange: (atBottom: boolean) => void;
  /** Ugras egy uzenetre: a hivo gorget es kiemeli (lasd `highlightId`). */
  onJumpTo: (messageId: string) => void;
  /**
   * Hozzaert egy uzenethez - eddig a sorig olvasottnak szamit, varakozas
   * nelkul. A hivo dolga eldonteni, mit kezd vele (lasd `ChannelView`).
   */
  onSeen: (seq: number) => void;
  actions: MessageActions;
  /** Permalinkkel megnyitott uzenet - atmenetileg kiemelve. */
  highlightId?: string | null;
}

/** A folyam elemei: naposztó, rendszeruzenet, vagy egy szerzo osszefuggo futama. */
type Item =
  | { kind: 'day'; key: string; iso: string }
  | { kind: 'system'; key: string; message: LocalMessage }
  | { kind: 'block'; key: string; userId: string | null; messages: LocalMessage[] };

/**
 * MINDEN UZENET SAJAT BUBOREKOT KAP - a FEJLEC viszont kozos.
 *
 * Ket dolgot kell egyszerre megoldani, es sokaig csak az egyik ment:
 *
 *  - **Minden uzenet lassek kulon.** Aki harom gondolatot ir le harom
 *    uzenetben, annal a harom nem olvadhat egyetlen szovegtombbe.
 *  - **Ne ismetlodjon a fejlec.** Ha minden uzenet fole odakerul a profilkep,
 *    a nev es az ido, akkor harom egymas utani "ok" harom teljes kartyat
 *    foglal - tobb helyet a fejlecek, mint a tartalom.
 *
 * A valasz: a SZERZOI FUTAM kap egy fejlecet, a futam uzenetei pedig kulon
 * buborekokban allnak alatta. A csoportositas tehat CSAK a fejlecre hat, a
 * vizualis elkulonulesre nem.
 *
 * Uj futam akkor kezdodik, ha mas ir, vagy ha {@link GROUP_WINDOW_MS}-nel
 * nagyobb szunet van. A valasz es a fontos uzenet NEM tori meg: az idezet a
 * buborekon BELUL all, a kiemeles pedig magat a buborekot szinezi - egyik sem
 * szorul kulon fejlecre.
 *
 * ─── A valasz helye ─────────────────────────────────────────────────────────
 *
 * A valasz UGYANOTT jelenik meg, ahol irtak: a folyam vegen, idorendben. Egy
 * ket nappal ezelotti uzenetre adott valasz kulonben ket nappal ezelotti
 * helyre kerulne - oda, ahova senki nem nez. Hogy MIRE valasz, azt a fole
 * tett idezet mondja el (lasd `QuotedParent` a MessageRow-ban).
 */
function partition(messages: LocalMessage[]): { items: Item[] } {
  const items: Item[] = [];
  let currentDay = '';

  for (const message of messages) {
    const day = dayKey(message.createdAt);
    if (day !== currentDay) {
      currentDay = day;
      items.push({ kind: 'day', key: `day-${day}`, iso: message.createdAt });
    }

    if (message.subtype !== 'standard') {
      items.push({ kind: 'system', key: message.id, message });
      continue;
    }

    const last = items[items.length - 1];
    const folytatas =
      last?.kind === 'block' &&
      last.userId === message.userId &&
      // Ket kulonbozo vendeg (H2) ugyanazon a "Vendeg" boton at ir - ne folyjon ossze.
      last.messages[0]!.meta?.guestId === message.meta?.guestId &&
      new Date(message.createdAt).getTime() -
        new Date(last.messages[last.messages.length - 1]!.createdAt).getTime() <
        GROUP_WINDOW_MS;

    if (folytatas && last.kind === 'block') last.messages.push(message);
    else
      items.push({ kind: 'block', key: message.id, userId: message.userId, messages: [message] });
  }

  return { items };
}

export function MessageList({
  channelId,
  isDirect,
  isNotes,
  messages,
  offline = false,
  usersById,
  meId,
  canManage,
  lastReadSeq,
  hasMore,
  loadingOlder,
  onLoadOlder,
  hasMoreNewer,
  loadingNewer,
  onLoadNewer,
  onJumpToLatest,
  scrollToBottomToken,
  onAtBottomChange,
  onJumpTo,
  onSeen,
  actions,
  highlightId,
}: MessageListProps) {
  /** Ugras datumra (U6): a kattintott nap - nyitva a valaszto. */
  const [jumpFrom, setJumpFrom] = useState<Date | null>(null);
  /*
    A legutolso sajat uzenet.

    Csatornaban CSAK ez ala kerul a "latta" jelzes: uzenetenkent kiirva
    elnyomna magat a beszelgetest, es egy harminc fos csatornaban folyamatos
    megfigyeles-erzetet keltene.
  */
  const lastOwnId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];
      if (message && message.userId === meId && !message.deletedAt) return message.id;
    }
    return null;
  }, [messages, meId]);

  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);
  const highlightIdRef = useRef(highlightId ?? null);
  highlightIdRef.current = highlightId ?? null;
  const previousHeight = useRef(0);
  const previousCount = useRef(0);

  const { items } = useMemo(() => partition(messages), [messages]);

  /**
   * Az idezethez kell a SZULO uzenet.
   *
   * Amit betoltottunk, azt innen vesszuk - halozat nelkul. Ami regebbi a
   * lapozott ablaknal, azt a `QuotedParent` kerdezi le magatol.
   */
  const byId = useMemo(() => new Map(messages.map((m) => [m.id, m])), [messages]);

  /**
   * Gorgetett-e mar LEFELE a felhasznalo ezen a latogatason.
   *
   * Az "Új üzenetek" vonal enelkul egy masodperc alatt eltunt: alul allva a
   * csatorna olvasott lett, es a hatar vele lepett - aki sok olvasatlannal
   * jott vissza, nem tudta, honnan nem olvasott. Most a vonal addig marad,
   * amig o maga el nem indul lefele (lasd `noteScrollIntent`).
   */
  const scrolledDown = useRef(false);

  /**
   * Az olvasatlan hatar: eddig van "uj uzenet" keret es innen indul az
   * "ÚJ ÜZENETEK" valasztovonal.
   *
   * A hatar a BELEPESKORI allapotbol indul, es csak akkor all arrebb, amikor
   * a felhasznalo mar lefele gorgetett, ES az olvasottsag tenylegesen
   * elorelepett (alulra ert, lasd READ_DWELL_MS). Ha nem kell gorgetnie, mert
   * minden olvasatlan elfer a kepernyon, a vonal a latogatas vegeig marad.
   *
   * Nem kovetjuk viszont VISSZAFELE: ha valaki masik gepen visszaallitana az
   * olvasottsagot, attol itt nem ugranak elo ujra a keretek.
   */
  const boundary = useRef<{ channelId: string; seq: number } | null>(null);
  if (!boundary.current || boundary.current.channelId !== channelId) {
    boundary.current = { channelId, seq: lastReadSeq };
  } else if (
    (lastReadSeq > boundary.current.seq && scrolledDown.current) ||
    // Kivetel: ha ITT jelolte olvatlannak, a vonal oda all, ahonnan olvatlan.
    (lastReadSeq < boundary.current.seq && isUnreadHeld(channelId))
  ) {
    boundary.current = { channelId, seq: lastReadSeq };
  }
  const entrySeq = boundary.current.seq;

  /*
    A belepeskor mar meglevo uzenetek vege.

    A vonal alatt csak a BELEPESKOR olvasatlanok kapnak keretet, meg az, ami
    azota jott es meg most is olvasatlan. Ami kozben jott es alul allva el is
    olvastuk, az nem: egy orakig nyitva tartott csatornaban kulonben minden uj
    uzenet keretet kapna.
  */
  const entryLastSeq = useRef<number | null>(null);
  if (entryLastSeq.current === null && messages.length > 0) {
    entryLastSeq.current = messages.reduce((max, m) => (m.pending ? max : Math.max(max, m.seq)), 0);
  }
  const isNewForMe = useCallback(
    (m: LocalMessage) =>
      !m.pending &&
      m.seq > entrySeq &&
      (m.seq <= (entryLastSeq.current ?? Infinity) || m.seq > lastReadSeq),
    [entrySeq, lastReadSeq],
  );

  /*
    AZ OSSZEFOGLALO a BELEPESKORI olvasatlanokhoz kotodik, nem a mozgo hatarhoz.

    A hatar egy masodperc mulva elorelep (a csatorna olvasott lesz), es vele
    eltunne a gomb is - pont akkor, amikor az ember elolvasna. Ezert a
    csatornaba lepeskor rogzitjuk, honnan volt olvasatlan, es a latogatas
    vegeig ott marad.
  */
  const recapEntry = useRef<{ channelId: string; afterSeq: number } | null>(null);
  if (!recapEntry.current || recapEntry.current.channelId !== channelId) {
    recapEntry.current = { channelId, afterSeq: lastReadSeq };
  }
  const recapAfter = recapEntry.current.afterSeq;
  const recapAt = useMemo(() => {
    const unread = messages.filter(
      (m) => !m.pending && m.seq > recapAfter && m.subtype === 'standard' && m.userId !== meId,
    );
    if (unread.length < RECAP_MIN_UNREAD) return null;
    return { firstSeq: unread[0]!.seq, afterSeq: recapAfter, count: unread.length };
  }, [messages, recapAfter, meId]);

  /*
    A vonal az elso olyan uzenet ele kerul, ami NEKEM uj: masok uzenete, ami
    belepeskor olvasatlan volt, vagy azota jott es meg nem olvastam. A sajat
    uzenetem, es amit itt allva elo elolvastam, nem nyit "Új üzenetek" vonalat.
  */
  const firstUnreadSeq = useMemo(() => {
    const first = messages.find((m) => isNewForMe(m) && m.userId !== meId);
    return first && messages.some((m) => !m.pending && m.seq <= entrySeq) ? first.seq : null;
  }, [messages, entrySeq, isNewForMe, meId]);

  /**
   * Az olvasatlanok - es kozuluk az, ami RAM tartozik.
   *
   * A "valaszt kaptam egy korabbi uzenetemre" eset az, amit a legnehezebb
   * megtalalni: a valasz a szuloje ala feszkelodik, tehat akar szaz sorral
   * feljebb is lehet. Ezert ha van ilyen, az ugras ODA visz, nem csak az elso
   * olvasatlanra.
   */
  const unread = useMemo(() => {
    const own = new Set(messages.filter((m) => m.userId === meId).map((m) => m.id));

    // A szamlalo az ELO olvasottsagot koveti: a vonal maradhat, de amit mar
    // elolvastunk, azt a gomb ne szamolja olvasatlannak.
    const read = Math.max(entrySeq, lastReadSeq);
    const items = messages.filter(
      (m) => !m.pending && !m.deletedAt && m.seq > read && m.userId !== meId,
    );

    const toMe = items.find((m) => m.parentId && own.has(m.parentId)) ?? null;

    return { count: items.length, first: items[0] ?? null, toMe };
  }, [messages, entrySeq, lastReadSeq, meId]);

  /*
    A belepeskor az elso olvasatlanhoz gorgettunk: ilyenkor az "N olvasatlan
    üzenet · Ugrás" gomb felesleges, hiszen pont ott allunk. Az elso
    felhasznaloi gorgetessel visszater.
  */
  const [anchoredToUnread, setAnchoredToUnread] = useState(false);

  const [atBottom, setAtBottom] = useState(true);

  const announcement = useNewMessageAnnouncement(
    channelId,
    messages,
    meId,
    usersById,
    hasMoreNewer,
  );

  const fullActions: MessageActions = actions;

  /*
    "Alul vagyunk" ket dolgot jelent egyszerre: a gorgetosav alul all, ES a
    betoltott ablak vege a beszelgetes vege is. Permalink-ablakban a masodik
    nem teljesul - ilyenkor nem szabad az aljara tapadni, kulonben minden uj
    uzenet lerantana a felhasznalot egy fel eves beszelgetes vegere.
  */
  const isAtBottom = useCallback(
    (el: HTMLDivElement) =>
      !hasMoreNewer && el.scrollHeight - el.scrollTop - el.clientHeight < STICK_THRESHOLD_PX,
    [hasMoreNewer],
  );

  /*
    A FELHASZNALO gorget-e lefele, vagy a kod.

    A gorgetesi esemeny nem mondja meg, ki inditotta: a regebbi oldal elore
    fuzese, a kepek betoltese, az aljara tapadas mind gorgetes. Ezert a
    lefele szandekot a bemenetbol olvassuk ki (gorgo, erintes, billentyu,
    gorgetosav-huzas), es csak az azt koveto lefele mozdulas szamit.
  */
  const lastScrollTop = useRef(0);
  const intent = useRef<{ at: number; down: boolean }>({ at: 0, down: false });
  const draggingScrollbar = useRef(false);
  const touchY = useRef<number | null>(null);
  const noteScrollIntent = useCallback((down: boolean) => {
    intent.current = { at: Date.now(), down };
    // Barmerre indul, a "pont itt allsz" allapotnak vege: johet az ugro gomb.
    setAnchoredToUnread(false);
  }, []);

  useEffect(() => {
    const stopDrag = () => {
      draggingScrollbar.current = false;
    };
    window.addEventListener('pointerup', stopDrag);
    return () => window.removeEventListener('pointerup', stopDrag);
  }, []);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const top = el.scrollTop;
    const userDown =
      draggingScrollbar.current ||
      (intent.current.down && Date.now() - intent.current.at < SCROLL_INTENT_MS);
    if (userDown && top > lastScrollTop.current) scrolledDown.current = true;
    lastScrollTop.current = top;

    const bottom = isAtBottom(el);
    if (bottom !== atBottomRef.current) {
      atBottomRef.current = bottom;
      setAtBottom(bottom);
      onAtBottomChange(bottom);
    }

    if (el.scrollTop < 80 && hasMore && !loadingOlder) {
      previousHeight.current = el.scrollHeight;
      onLoadOlder();
    }

    // Lefele ugyanaz, mint felfele: a vegehez erve jon a kovetkezo oldal.
    if (
      hasMoreNewer &&
      !loadingNewer &&
      el.scrollHeight - el.scrollTop - el.clientHeight < STICK_THRESHOLD_PX
    ) {
      onLoadNewer();
    }
  }, [
    hasMore,
    hasMoreNewer,
    isAtBottom,
    loadingNewer,
    loadingOlder,
    onAtBottomChange,
    onLoadNewer,
    onLoadOlder,
  ]);

  /*
    CELUZENETRE UGRAS: az aljara tapadas MEGSZUNIK.

    Egy permalinkre (mentett uzenet, keresesi talalat, ertesites) erkezve a lista elobb az
    aljara allt, es a ResizeObserver minden tartalomnovekedesnel ujra odarantotta - a
    celuzenetre inditott gorgetest igy felulirta: a felhasznalo a csatorna aljan kotott
    ki, nem a keresett uzenetnel. Amig kiemelt uzenet van, nem tapadunk alulra; a sor maga
    tartja magat kozepen (lasd MessageRow).
  */
  useLayoutEffect(() => {
    if (!highlightId) return;
    previousHeight.current = 0;
    if (!atBottomRef.current) return;
    atBottomRef.current = false;
    setAtBottom(false);
    onAtBottomChange(false);
  }, [highlightId, onAtBottomChange]);

  /**
   * Ket kulon eset, ezert fut layout-fazisban (villogas nelkul):
   *  - regebbi uzenetek elore fuzese: a gorgetesi pozicio helyben marad,
   *  - uj uzenet a vegen: csak akkor gorgetunk, ha a felhasznalo alul volt.
   */
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    if (previousHeight.current > 0 && el.scrollHeight > previousHeight.current) {
      el.scrollTop += el.scrollHeight - previousHeight.current;
      previousHeight.current = 0;
      return;
    }

    if (messages.length !== previousCount.current) {
      const grew = messages.length > previousCount.current;
      previousCount.current = messages.length;
      /*
        A SAJAT uzenetunk utan MINDIG az aljara: aki kuldott, az latni akarja,
        mi ment el - akkor is, ha kozben kicsit feljebb gorgetett.
      */
      const last = messages[messages.length - 1];
      if (grew && last?.userId === meId) {
        atBottomRef.current = true;
        setAtBottom(true);
        onAtBottomChange(true);
      }
      if (atBottomRef.current) el.scrollTop = el.scrollHeight;
    }
    // A chat "kesz" pillanata a sebessegmereshez - csak az elso alkalommal szamit.
    if (messages.length > 0) markFirstMessages();
  }, [messages, meId, onAtBottomChange]);

  /*
    KERT ugras az aljara (lasd {@link MessageListProps.scrollToBottomToken}).

    A hivo akkor emeli a szamlalot, amikor a friss tartalom MAR a helyen van.
    Egy szandekolt ugrast nem bizunk a "sajat uzenet erkezett" heurisztikara:
    ha kozben az ablak is kicserelodott, az a szabaly siman alulmaradt egy
    masik gorgetes-szabalynak, es a felhasznalo a lap TETEJEN kotott ki.
  */
  const handledBottomToken = useRef(scrollToBottomToken);
  useLayoutEffect(() => {
    if (scrollToBottomToken === handledBottomToken.current) return;
    handledBottomToken.current = scrollToBottomToken;

    const el = containerRef.current;
    if (!el) return;
    // A "pozicio megorzese" szabaly ilyenkor nem ervenyes: nem lapoztunk
    // visszafele, hanem kicsereltuk az ablakot.
    previousHeight.current = 0;
    previousCount.current = messages.length;
    el.scrollTop = el.scrollHeight;
    atBottomRef.current = true;
    setAtBottom(true);
    onAtBottomChange(true);
  }, [scrollToBottomToken, messages, onAtBottomChange]);

  /*
    KESON NOVO TARTALOM: a kep, a GIF, a link-elonezet csak az uzenet UTAN
    tolt be, es lejjebb tolja a folyamot. Ha addig alul alltunk, ott is
    maradunk - kulonben az imen kuldott kepes uzenet alja levagodott.
  */
  useEffect(() => {
    const el = containerRef.current;
    const content = contentRef.current;
    if (!el || !content || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (atBottomRef.current) el.scrollTop = el.scrollHeight;
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  /*
    Permalink-ablakba lepve (es belole kilepve) azonnal ujra kell ertekelni,
    hogy "alul vagyunk-e". Kulonben a gorgetes elotti pillanatban meg a regi
    valasz elne: az ablak a beszelgetes kozepen all, de a nezet olvasottnak
    jelentene a csatornat es az uj uzenetekhez tapadna.
  */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const bottom = !hasMoreNewer && isAtBottom(el);
    if (bottom === atBottomRef.current) return;
    atBottomRef.current = bottom;
    setAtBottom(bottom);
    onAtBottomChange(bottom);
  }, [hasMoreNewer, isAtBottom, onAtBottomChange, messages]);

  /*
    BELEPESKOR AZ ELSO OLVASATLANHOZ.

    Alapbol az aljara allunk. Ha viszont az "Új üzenetek" vonal onnan nem
    latszana (tobb az olvasatlan, mint ami elfer), a vonalhoz gorgetunk, a
    teteje kozelebe - igy az ember ott kezdi, ahol abbahagyta, es mivel nem
    vagyunk alul, a csatorna sem lesz magatol olvasott.

    Ha az elso olvasatlan meg nincs betoltve, elobb visszafele lapozunk
    (legfeljebb UNREAD_ANCHOR_MAX_PAGES oldalt). Permalinkkel vagy kiemelt
    uzenettel erkezve nem nyulunk a gorgeteshez: ott az a cel.
  */
  const unreadDividerRef = useRef<HTMLDivElement>(null);
  const entryAnchor = useRef<'pending' | 'done'>('pending');
  const anchorPages = useRef(0);
  const anchoredRef = useRef(false);
  useLayoutEffect(() => {
    if (entryAnchor.current === 'done' || messages.length === 0) return;
    const el = containerRef.current;
    if (!el) return;

    const finish = () => {
      entryAnchor.current = 'done';
    };
    if (hasMoreNewer || highlightId) return finish();
    if (!messages.some((m) => !m.pending && m.seq > entrySeq && m.userId !== meId)) {
      return finish();
    }

    if (firstUnreadSeq === null) {
      // Minden betoltott uzenet olvasatlan: az eleje regebbi oldalon van.
      if (!hasMore || anchorPages.current >= UNREAD_ANCHOR_MAX_PAGES) return finish();
      if (!loadingOlder) {
        anchorPages.current += 1;
        onLoadOlder();
      }
      return;
    }

    const divider = unreadDividerRef.current;
    if (!divider) return finish();
    const dividerTop =
      divider.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop;
    const bottomTop = el.scrollHeight - el.clientHeight;
    finish();
    // Alulrol is latszik a vonal: maradunk alul, ahogy eddig.
    if (dividerTop >= bottomTop) return;

    el.scrollTop = Math.max(0, dividerTop - UNREAD_ANCHOR_OFFSET_PX);
    lastScrollTop.current = el.scrollTop;
    atBottomRef.current = false;
    setAtBottom(false);
    anchoredRef.current = true;
    setAnchoredToUnread(true);
    onAtBottomChange(false);
  }, [
    messages,
    entrySeq,
    firstUnreadSeq,
    hasMore,
    hasMoreNewer,
    highlightId,
    loadingOlder,
    meId,
    onAtBottomChange,
    onLoadOlder,
  ]);

  // Csatornavaltasnal alulra - hacsak a fenti mar az elso olvasatlanhoz nem allt.
  useEffect(() => {
    const el = containerRef.current;
    // Kiemelt (celzott) uzenetnel nem ugrunk az aljara: ott a cel a keresett uzenet.
    if (!el || anchoredRef.current || highlightIdRef.current) return;
    el.scrollTop = el.scrollHeight;
    atBottomRef.current = true;
    setAtBottom(true);
    onAtBottomChange(true);
  }, [onAtBottomChange]);

  /*
    Melyik lezart olelesrol van atirat?

    EGY kerdes az egesz kepernyore. Enelkul minden oleles mellett ott lenne a
    gomb, es a felenel csak annyi fogadna, hogy "nincs hozzaferesed, vagy nem
    keszult" - egy gomb, ami legtobbszor semmit nem ad, rosszabb, mintha ott
    sem lenne.
  */
  const huddleMessageIds = items.flatMap((item) =>
    item.kind === 'system' &&
    item.message.subtype === 'huddle_started' &&
    typeof item.message.meta?.huddleId === 'string'
      ? [item.message.meta.huddleId]
      : [],
  );
  const { data: transcriptStates } = useTranscriptStates(huddleMessageIds);
  /** A hivasok jegyzetvaszna (H4) - ugyanugy egy kerdessel, mint az atiratok. */
  const { data: huddleNotes } = useHuddleNotes(huddleMessageIds);

  /*
    Melyik telefonhivasrol van kulon "nem fogadott" sor.

    Annal a hivasnaplo sora felesleges - ugyanazt mondana ketszer. Ha viszont
    nincs (pl. a takarito zarta le a csengo hivast), a naplo sora mutatja.
  */
  const missedHuddleIds = new Set(
    items.flatMap((item) =>
      item.kind === 'system' &&
      item.message.subtype === 'huddle_missed' &&
      typeof item.message.meta?.huddleId === 'string'
        ? [item.message.meta.huddleId]
        : [],
    ),
  );

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {jumpFrom && (
        <JumpToDateDialog
          channelId={channelId}
          open
          onOpenChange={(open) => !open && setJumpFrom(null)}
          initial={jumpFrom}
        />
      )}
      {/*
        OLVASATLAN-SAV.

        Csak akkor jelenik meg, ha van olvasatlan ES nem a lista aljan allunk -
        alul ugyanis mar latjuk a legfrissebbet, ott a sav csak zaj lenne.

        A "valaszt kaptam" esetet KULON kezeli: az a legnehezebben megtalalhato,
        mert a valasz a szuloje ala feszkelodik, akar szaz sorral feljebb.
      */}
      {/*
        VISSZA A LEGFRISSEBBRE.

        Permalinkkel egy regi uzenetre ugorva a beszelgetes kozepen allunk -
        onnan vegiggorogni ezer sort nem ut. Ez a gomb egy lepesben visszahozza
        a vegét; enelkul a permalink zsakutca volt, amibol csak ujratoltessel
        lehetett kijonni.
      */}
      {hasMoreNewer && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center px-4 pb-3">
          <button
            type="button"
            onClick={onJumpToLatest}
            className="pointer-events-auto flex items-center gap-2 rounded-pill border border-border-soft bg-surface-raised px-3.5 py-1.5 text-xs font-medium shadow-(--shadow-lift) transition-transform duration-(--duration-quick) hover:-translate-y-px"
          >
            <span aria-hidden className="text-ink-faint">
              <Icon name="le" size="sm" />
            </span>
            Ugrás a legfrissebbre
          </button>
        </div>
      )}

      {!atBottom && !anchoredToUnread && unread.count > 0 && (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center px-4 pt-2">
          <button
            type="button"
            onClick={() => onJumpTo((unread.toMe ?? unread.first)!.id)}
            className="pointer-events-auto flex items-center gap-2 rounded-pill border border-border-soft bg-surface-raised px-3.5 py-1.5 text-xs font-medium shadow-(--shadow-lift) transition-transform duration-(--duration-quick) hover:-translate-y-px"
          >
            <span aria-hidden className="text-alert">
              <Icon name="nyilFel" size="sm" />
            </span>
            {unread.toMe ? 'Válasz érkezett az üzenetedre' : `${unread.count} olvasatlan üzenet`}
            <span className="text-ink-faint">· Ugrás</span>
          </button>
        </div>
      )}

      <div
        ref={containerRef}
        onScroll={handleScroll}
        onWheel={(event) => noteScrollIntent(event.deltaY > 0)}
        onTouchStart={(event) => {
          touchY.current = event.touches[0]?.clientY ?? null;
        }}
        onTouchMove={(event) => {
          const y = event.touches[0]?.clientY;
          if (y === undefined || touchY.current === null) return;
          // Az ujj felfele huz = a tartalom lefele gorog.
          noteScrollIntent(y < touchY.current);
          touchY.current = y;
        }}
        onKeyDown={(event) => {
          if (['ArrowDown', 'PageDown', 'End'].includes(event.key)) noteScrollIntent(true);
          else if (event.key === ' ' && event.target === event.currentTarget)
            noteScrollIntent(!event.shiftKey);
          else if (['ArrowUp', 'PageUp', 'Home'].includes(event.key)) noteScrollIntent(false);
        }}
        onPointerDown={(event) => {
          // A gorgetosavra kattintas celpontja maga a tarolo.
          if (event.target === event.currentTarget) {
            draggingScrollbar.current = true;
            noteScrollIntent(false);
          }
        }}
        // A min-h-0 nelkul a flex-elem nem tud a tartalma ala zsugorodni, es a
        // gorgetes helyett kitolna a szerkesztosavot a kepernyorol.
        className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-2 sm:px-6"
      >
        <div ref={contentRef} className="mx-auto flex min-h-full max-w-4xl flex-col justify-end">
          {hasMore && (
            <div className="py-3 text-center">
              <button
                type="button"
                onClick={onLoadOlder}
                disabled={loadingOlder}
                className="rounded-pill border border-border-soft bg-surface-raised px-3.5 py-1.5 text-xs text-ink-muted shadow-(--shadow-soft) transition-[translate,box-shadow,opacity] duration-(--duration-base) ease-(--ease-soft) hover:-translate-y-px hover:shadow-(--shadow-lift) disabled:opacity-50"
              >
                {loadingOlder ? 'Töltés…' : 'Régebbi üzenetek'}
              </button>
            </div>
          )}

          {items.map((item) => {
            if (item.kind === 'day') {
              return (
                <DayDivider
                  key={item.key}
                  iso={item.iso}
                  onJump={() => setJumpFrom(new Date(item.iso))}
                />
              );
            }
            if (item.kind === 'system')
              return item.message.subtype === 'huddle_missed' ? (
                <MissedCallRow key={item.key} message={item.message} meId={meId} />
              ) : item.message.subtype === 'huddle_started' && item.message.meta?.phone ? (
                <PhoneCallRow
                  key={item.key}
                  meId={meId}
                  message={item.message}
                  transcriptState={
                    typeof item.message.meta.huddleId === 'string'
                      ? transcriptStates?.[item.message.meta.huddleId]
                      : undefined
                  }
                  hasMissedRow={
                    typeof item.message.meta.huddleId === 'string' &&
                    missedHuddleIds.has(item.message.meta.huddleId)
                  }
                  notesCanvasId={
                    typeof item.message.meta.huddleId === 'string'
                      ? huddleNotes?.[item.message.meta.huddleId]
                      : undefined
                  }
                />
              ) : item.message.subtype === 'huddle_poll' ? (
                <HuddlePollResultRow key={item.key} message={item.message} />
              ) : item.message.subtype === 'calendar' ? (
                <CalendarMessageCard key={item.key} message={item.message} />
              ) : item.message.subtype === 'huddle_started' ? (
                <HuddleStartedRow
                  key={item.key}
                  message={item.message}
                  meId={meId}
                  transcriptState={
                    typeof item.message.meta?.huddleId === 'string'
                      ? transcriptStates?.[item.message.meta.huddleId]
                      : undefined
                  }
                  notesCanvasId={
                    typeof item.message.meta?.huddleId === 'string'
                      ? huddleNotes?.[item.message.meta.huddleId]
                      : undefined
                  }
                />
              ) : (
                <SystemRow key={item.key} text={item.message.text} iso={item.message.createdAt} />
              );

            const showUnread =
              firstUnreadSeq !== null && item.messages.some((m) => m.seq === firstUnreadSeq);

            /*
            Az olvasatlan futam halvany sav-jelolest kap.

            Nem szinezzuk teli hattérrel: harminc olvasatlan uzenetnel az
            olvashatatlan lenne. Egy vekony, bal oldali sav eleg ahhoz, hogy
            vegigfutva a szemunkkel lassuk, hol kezdodik az uj resz.
          */
            const isUnread = item.messages.some((m) => isNewForMe(m) && m.userId !== meId);

            return (
              <div
                key={item.key}
                className={
                  isUnread
                    ? 'border-l-2 border-alert/50 pl-2 -ml-2 rounded-l-sm bg-alert-soft/25'
                    : ''
                }
              >
                {showUnread && <UnreadDivider ref={unreadDividerRef} />}
                {recapAt !== null && item.messages.some((m) => m.seq === recapAt.firstSeq) && (
                  <ChannelRecap
                    channelId={channelId}
                    afterSeq={recapAt.afterSeq}
                    unread={recapAt.count}
                  />
                )}
                <MessageBlock
                  userId={item.userId}
                  author={item.userId ? usersById.get(item.userId) : undefined}
                  messages={item.messages}
                  meId={meId}
                  usersById={usersById}
                  canManage={canManage}
                  actions={fullActions}
                  loadedById={byId}
                  onJumpTo={onJumpTo}
                  highlightId={highlightId ?? null}
                  channelId={channelId}
                  isDirect={isDirect}
                  lastOwnId={lastOwnId}
                  onSeen={onSeen}
                />
              </div>
            );
          })}

          {/*
            A FRISSEN erkezett uzenet bemondasa - lasd useNewMessageAnnouncement.
            Mindig a DOM-ban van, kulonben az elso bemondas elveszne.
          */}
          <div role="status" className="sr-only">
            {announcement}
          </div>

          {messages.length === 0 &&
            (offline ? <OfflineNotice /> : <EmptyState isNotes={isNotes} />)}

          {hasMoreNewer && messages.length > 0 && (
            <div className="py-3 pb-12 text-center">
              <button
                type="button"
                onClick={onLoadNewer}
                disabled={loadingNewer}
                className="rounded-pill border border-border-soft bg-surface-raised px-3.5 py-1.5 text-xs text-ink-muted shadow-(--shadow-soft) transition-[translate,box-shadow,opacity] duration-(--duration-base) ease-(--ease-soft) hover:-translate-y-px hover:shadow-(--shadow-lift) disabled:opacity-50"
              >
                {loadingNewer ? 'Töltés…' : 'Újabb üzenetek'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Ennyinel regebbi uzenetet mar nem mondunk be "ujkent". */
const ANNOUNCE_MAX_AGE_MS = 60_000;

/**
 * Kepernyoolvasonak: a beszelgetes VEGERE erkezo, masoktol jovo friss uzenet.
 *
 * NEM az egesz lista az elo regio (`role="log"`): regebbi oldalak betoltesekor,
 * lapozaskor es csatornavaltaskor egesz uzenetkotegeket olvasna fel. Itt csak
 * az szamit, ami a MAR LATOTT utolso uzenet UTAN jott, es egy percnel nem
 * regebbi - a "kesobbi oldal betoltese" ugyanis szinten a vegere fuz, de
 * regi uzeneteket.
 */
function useNewMessageAnnouncement(
  channelId: string,
  messages: LocalMessage[],
  meId: string,
  usersById: Map<string, PublicUser>,
  hasMoreNewer: boolean,
): string {
  const tail = useRef<{ channelId: string; id: string | null } | null>(null);
  const [text, setText] = useState('');
  const { nameOf } = useUsers();

  useEffect(() => {
    const last = messages[messages.length - 1] ?? null;
    const previous = tail.current;
    tail.current = { channelId, id: last?.id ?? null };

    // Csatornavaltas vagy elso betoltes: amit most latunk, az nem "uj".
    if (!previous || previous.channelId !== channelId) {
      setText('');
      return;
    }
    if (hasMoreNewer || !previous.id || previous.id === last?.id) return;

    const from = messages.findIndex((m) => m.id === previous.id);
    // Az ablak kicserelodott (ugras, ujratoltes) - nincs mihez merni.
    if (from === -1) return;

    const now = Date.now();
    const fresh = messages
      .slice(from + 1)
      .filter(
        (m) =>
          m.userId !== meId &&
          !m.pending &&
          !m.deletedAt &&
          m.subtype === 'standard' &&
          now - new Date(m.createdAt).getTime() < ANNOUNCE_MAX_AGE_MS,
      );
    const newest = fresh[fresh.length - 1];
    if (!newest) return;

    const name = newest.userId ? nameOf(newest.userId, 'Valaki') : 'Valaki';
    const body = newest.text.length > 200 ? `${newest.text.slice(0, 200)}…` : newest.text;
    const intro = fresh.length > 1 ? `${fresh.length} új üzenet. Legutóbb ` : 'Új üzenet: ';
    setText(`${intro}${name}: ${body || 'melléklet'}`);
  }, [channelId, messages, meId, usersById, nameOf, hasMoreNewer]);

  return text;
}

function MessageBlock({
  userId,
  author,
  messages,
  meId,
  usersById,
  canManage,
  actions,
  loadedById,
  onJumpTo,
  highlightId,
  channelId,
  isDirect,
  lastOwnId,
  onSeen,
}: {
  userId: string | null;
  author: PublicUser | undefined;
  /** A futam uzenetei - kozos fejleccel, kulon buborekokban. */
  messages: LocalMessage[];
  meId: string;
  usersById: Map<string, PublicUser>;
  canManage: boolean;
  actions: MessageActions;
  /** A mar betoltott uzenetek - az idezet ebbol keresi ki a szulot. */
  loadedById: Map<string, LocalMessage>;
  onJumpTo: (messageId: string) => void;
  highlightId: string | null;
  channelId: string;
  isDirect: boolean;
  lastOwnId: string | null;
  onSeen: (seq: number) => void;
}) {
  const { nameOf } = useUsers();
  /**
   * A webhook uzenetenkent felulirhatja a megjeleno nevet.
   *
   * Egy ugyviteli rendszer igy tud tobbfele nevben irni ("Rendelo-2",
   * "Laborsystem") egyetlen webhookkal - a folyamban mindig latszik, honnan
   * jott a bejegyzes.
   */
  const elso = messages[0]!;
  const override = elso.meta?.username;
  const name = (typeof override === 'string' && override.trim()) || nameOf(author, 'Ismeretlen');

  const isBot = author?.isBot === true;

  /*
    A futam LEGNAGYOBB sorszama - erre ugrik az olvasottsag, ha valaki
    hozzaer. A meg el nem kuldott uzenetnek nincs ervenyes sorszama.
  */
  const seq = messages.reduce((max, m) => (m.pending ? max : Math.max(max, m.seq)), 0);

  return (
    <article
      /*
        HOZZAERES = elolvasta.

        A varakozasos jeloles (lasd READ_DWELL_MS) az akaratlan pillantasok
        ellen szol; aki viszont rakoppint egy uzenetre, az nem "atfutott"
        rajta. A `Capture` fazis azert kell, hogy a szamitas akkor is
        megtortenjen, ha belul egy link vagy gomb megallitja az esemenyt.
      */
      onPointerDownCapture={() => onSeen(seq)}
      /*
        A FUTAMNAK nincs kerete es nincs hattere.

        A doboz a BUBOREK koré kerul, uzenetenkent (lasd `MessageRow`) - itt
        csak a fejlec all, es az alatta sorakozo buborekok. Enelkul doboz
        lenne a dobozban: egy keret a futam korul, egy masik minden
        uzenet korul.
      */
      className="mt-3"
    >
      <header className="flex items-center gap-2.5">
        {/*
          A profilkep es a nev is DM-et nyit az illetovel - ez a leggyakoribb
          kovetkezo lepes, ha valakinek az uzenetet olvasod.
        */}
        <UserLink
          userId={userId}
          meId={meId}
          disabled={isBot || Boolean(override)}
          label={name}
          className="shrink-0 rounded-[0.6rem]"
        >
          <Avatar
            userId={userId ?? 'ismeretlen'}
            name={name}
            avatarUrl={author?.avatarUrl}
            size="md"
          />
        </UserLink>
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <UserLink
              userId={userId}
              meId={meId}
              disabled={isBot || Boolean(override)}
              label={name}
              className="truncate font-semibold hover:underline"
            >
              {name}
            </UserLink>
            {/* Latszodjon, ha nem ember irta - ez bizalmi kerdes, nem diszites. */}
            {/* A vendeg (H2) a "Vendeg" boton at ir, de ember - a neve mellett ott a "(vendég)". */}
            {isBot && !elso.meta?.guestId && (
              <span className="shrink-0 rounded-pill bg-surface-sunken px-1.5 py-px text-2xs font-medium text-ink-faint">
                app
              </span>
            )}
            <time className="shrink-0 text-2xs text-ink-faint" dateTime={elso.createdAt}>
              {formatTime(elso.createdAt)}
            </time>
            {/*
              A BEOSZTAS a nev melle kerult, nem ala.

              Kulon sorban egy egesz sornyi helyet vitt el minden futamnal -
              a fejlec igy ketszer olyan magas volt, mint amennyi informaciot
              hordozott. Egy sorban, halkan, csonkolva ugyanannyit mond.
            */}
            <span className="min-w-0 truncate text-2xs text-ink-faint">
              <UserStatus user={author} fallback={author?.title ?? null} />
            </span>
          </div>
        </div>
      </header>

      {/*
        A buborekok a PROFILKEP oszlopa alatt sorakoznak.

        A behuzas pontosan a kep szelessege (2,25rem) + a fejlec terkoze
        (0,625rem): igy a buborekok bal szele a NEV ala esik, nem a kep ala -
        a szem egy fuggoleges vonal menten fut vegig a futamon.
      */}
      <div className="mt-1 flex flex-col gap-2 pl-[2.875rem]">
        {messages.map((message) => (
          <MessageRow
            key={message.id}
            message={message}
            meId={meId}
            usersById={usersById}
            canManage={canManage}
            actions={actions}
            loadedById={loadedById}
            onJumpTo={onJumpTo}
            highlighted={highlightId === message.id}
            status={
              message.userId === meId && !message.deletedAt ? (
                <MessageStatus
                  channelId={channelId}
                  seq={message.seq}
                  senderId={meId}
                  pending={message.pending}
                  failed={message.failed}
                  isDirect={isDirect}
                  isLastOwn={message.id === lastOwnId}
                />
              ) : null
            }
          />
        ))}
      </div>
    </article>
  );
}

/**
 * Huddle-inditas a folyamban.
 *
 * Nem sima rendszeruzenet: amig a hivas tart, INNEN is be lehet lepni. A
 * csatorna elozmenyeben igy latszik, mikor es ki hivott - a huddle nem tunik
 * el nyomtalanul, ahogy a Slackben.
 */
function HuddleStartedRow({
  message,
  meId,
  transcriptState,
  notesCanvasId,
}: {
  message: Message;
  meId: string;
  /** `undefined` = nincs (vagy nem lathatjuk) atirat: a gomb sem jelenik meg. */
  transcriptState?: TranscriptState;
  /** A hivas jegyzetvaszna (H4), ha keszult. */
  notesCanvasId?: string;
}) {
  const { data: huddles } = useActiveHuddles();

  const huddleId = typeof message.meta?.huddleId === 'string' ? message.meta.huddleId : null;
  const live = huddles?.find((h) => h.id === huddleId) ?? null;
  // A hivas a kulon ablakban fut - a bent-letet a huddle resztvevoibol nezzuk.
  const amInside =
    live?.participants.some((p) => p.user.id === meId && p.state === 'joined') ?? false;

  return (
    <div className="flex items-center gap-2 px-3.5 py-1.5 text-xs text-ink-faint">
      <span aria-hidden className="text-ink-faint/60">
        <Icon name="huddle" />
      </span>
      <span className={live ? 'text-ink-muted' : undefined}>{message.text}</span>

      {live && !amInside && (
        <button
          type="button"
          // MINDIG a hivas-ablakban: a fo ablakbol inditott hivast az "Ablak" gomb
          // atvetele megszakitotta volna.
          onClick={() => void openHuddleWindow(message.channelId)}
          className="rounded-pill bg-accent px-2 py-0.5 text-2xs font-semibold text-accent-ink transition-colors duration-(--duration-quick) hover:bg-accent-hover disabled:opacity-50"
        >
          Csatlakozás
        </button>
      )}
      {live && amInside && <span className="text-2xs text-accent">bent vagy</span>}
      {!live && <span className="text-2xs opacity-70">(befejeződött)</span>}

      {/*
        Az ATIRAT innen erheto el - de CSAK ott, ahol tenyleg van mit megnyitni.

        Korabban minden lezart oleles mellett ott volt a gomb, es a felenel
        csak annyi fogadott, hogy "nincs hozzaferesed, vagy nem keszult". Egy
        gomb, ami legtobbszor semmit nem ad, rosszabb, mintha ott sem lenne.
        A `transcriptState` a szervertol jon, jogosultsaggal egyutt szurve.
      */}
      {!live && huddleId && transcriptState && (
        <TranscriptButton huddleId={huddleId} transcriptState={transcriptState} />
      )}
      {notesCanvasId && <NotesButton channelId={message.channelId} canvasId={notesCanvasId} />}

      <time className="ml-auto text-2xs opacity-70" dateTime={message.createdAt}>
        {formatTime(message.createdAt)}
      </time>
    </div>
  );
}

/**
 * A hivas JEGYZETVASZNA (versenyaudit H4) - a hivas alatt es utan is.
 * A csatorna vaszonpaneljet nyitja ezzel a vaszonnal (`?canvas=`).
 */
function NotesButton({ channelId, canvasId }: { channelId: string; canvasId: string }) {
  return (
    <Link
      to="/c/$channelId"
      params={{ channelId }}
      search={{ canvas: canvasId }}
      title="A hívás közös jegyzete"
      className="rounded-pill border border-accent/40 bg-accent-soft px-2 py-0.5 text-2xs font-medium text-accent transition-colors hover:bg-accent hover:text-accent-ink"
    >
      <Icon name="canvas" size="sm" /> Jegyzetvászon
    </Link>
  );
}

/**
 * Az ATIRAT gombja egy lezart hivas soran - az oleles es a telefonhivas soran
 * ugyanaz.
 */
function TranscriptButton({
  huddleId,
  transcriptState,
}: {
  huddleId: string;
  transcriptState: TranscriptState;
}) {
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        disabled={transcriptState === 'processing'}
        onClick={() => setTranscriptOpen(true)}
        title={
          transcriptState === 'processing'
            ? 'Az átirat még készül'
            : transcriptState === 'failed'
              ? 'Az átirat nem készült el'
              : 'Az ölelés átirata'
        }
        className={`rounded-pill border px-2 py-0.5 text-2xs font-medium transition-colors ${
          transcriptState === 'processing'
            ? 'cursor-default border-border-soft text-ink-faint'
            : /*
                KESZ allapotban a gomb az akcentust viseli - ugyanugy, mint
                a tobbi tenyleges muvelet. A szurke azt jelenti: "meg nem",
                nem azt, hogy "masodrendu".
              */
              'border-accent/40 bg-accent-soft text-accent hover:bg-accent hover:text-accent-ink'
        }`}
      >
        <Icon name="atirat" size="sm" />{' '}
        {transcriptState === 'processing'
          ? 'Átirat készül…'
          : transcriptState === 'failed'
            ? 'Átirat — hiba'
            : 'Átirat'}
      </button>
      {transcriptOpen && (
        <TranscriptDialog
          huddleId={huddleId}
          open={transcriptOpen}
          onOpenChange={setTranscriptOpen}
        />
      )}
    </>
  );
}

/**
 * TELEFONHIVAS a vonal csatornajaban - a hivasnaplo egy sora.
 *
 * Amig a hivas el, az elo huddle-bol mutatja, hol tart (csorog, hivasban,
 * mennyi ideje), es innen be lehet lepni. Lezaras utan a szerver a `meta.call`
 * mezobe irja, ami megmarad: mikor, mennyi ideig, ki vette fel, kik beszeltek,
 * melyik szamrol hivtak.
 */
function PhoneCallRow({
  meId,
  message,
  hasMissedRow,
  transcriptState,
  notesCanvasId,
}: {
  meId: string;
  message: Message;
  hasMissedRow: boolean;
  transcriptState?: TranscriptState;
  notesCanvasId?: string;
}) {
  const { users, nameOf } = useUsers();
  const { data: huddles } = useActiveHuddles();
  const callBack = useDialFromChannel(message.channelId, meId);
  const phoneContacts = usePhoneContacts();
  const canCallOut = users.find((user) => user.id === meId)?.canCallOut ?? false;
  const customerSystem = useCustomerSystem();

  const meta = message.meta ?? {};
  const huddleId = typeof meta.huddleId === 'string' ? meta.huddleId : null;
  const legId = typeof meta.legId === 'string' ? meta.legId : null;
  const call = (meta.call ?? null) as PhoneCallLog | null;
  const live = call ? null : (huddles?.find((h) => h.id === huddleId) ?? null);
  // Kimeno hivasnal a sor a SAJAT agahoz tartozik (egy hivasban tobb is lehet).
  const leg =
    (legId ? live?.phoneLegs.find((item) => item.id === legId) : live?.phoneLegs[0]) ?? null;
  const elapsed = useElapsed(leg?.state === 'active' ? leg.answeredAt : null);
  const [noteOpen, setNoteOpen] = useState(false);

  // A nem fogadott hivasnak sajat, kiemelt sora van - ketszer ne mondjuk el.
  if (call?.outcome === 'missed' && hasMissedRow) return null;

  const callerNumber =
    call?.callerNumber ?? (typeof meta.callerNumber === 'string' ? meta.callerNumber : null);
  const lineLabel = call?.lineLabel ?? leg?.lineLabel ?? null;
  /*
    KI HIVOTT - ketfele forrasbol, ebben a sorrendben.

    1. A helyi cimjegyzek: amit egy kolléga KEZZEL irt be, az erosebb.
    2. A hivas naploja: a TT altal a hivas idejen azonositott ugyfel neve.

    A ketto kulon marad, mert mast jelentenek: a masodikat nem lehet atirni,
    es nem is kell - ha megvan, nincs mit hozzarendelni.
  */
  const contactName = phoneContacts.nameOf(callerNumber) ?? call?.customerName ?? null;
  // A vonal csatornajabol DM-be masolt naplosor (lasd calllog.ts).
  const isMirror = typeof meta.mirrorOf === 'string';
  // A hivas-ablakban ulunk-e (a fo ablak sajat huddle-allapota itt ures).
  const amInside =
    live?.participants.some((p) => p.user.id === meId && p.state === 'joined') ?? false;
  const outbound = (call?.direction ?? meta.direction) === 'outbound';
  const missed = call?.outcome === 'missed' || call?.outcome === 'no_answer';

  let detail: string;
  if (call) {
    // Atadasnal kollegankent a bent toltott ido: "Anna 1:10 → Bela 2:05".
    const names =
      call.talkers && call.talkers.length > 1
        ? call.talkers
            .map((t) => `${nameOf(t.userId)} ${formatCallDuration(t.seconds)}`)
            // Egyutt bent voltak: vesszo; egymas utan (atadas): nyil.
            .join(call.together === false ? ' → ' : ', ')
        : call.talkedBy.map((id) => nameOf(id)).join(', ');
    detail =
      call.outcome === 'no_answer'
        ? `Nem vették fel · kicsengett ${formatCallDuration(call.ringSeconds)}${call.answeredBy ? ` · hívta: ${nameOf(call.answeredBy)}` : ''}`
        : missed
          ? `Nem fogadott hívás · csengett ${formatCallDuration(call.ringSeconds)}`
          : `${formatCallDuration(call.talkSeconds)}${names ? ` · ${names}` : ''}`;
  } else if (leg && leg.state === 'ringing') {
    detail = outbound
      ? `Kicsöng…${leg.answeredBy ? ` · hívja: ${nameOf(leg.answeredBy)}` : ''}`
      : 'Csörög…';
  } else if (leg && leg.state === 'active') {
    detail = `Hívásban · ${elapsed}${leg.answeredBy ? ` · ${nameOf(leg.answeredBy)}` : ''}`;
  } else if (leg && outbound) {
    detail = leg.answeredAt ? 'Bontva' : 'Nem vették fel';
  } else if (live) {
    detail = 'A hívó letette';
  } else {
    detail = 'Befejeződött';
  }

  const start = call?.startedAt ?? message.createdAt;

  return (
    <div
      /*
        `flex-wrap`: a kinyitott "ugyfelhez kotom" panel sajat sorba kerul, es
        nem szoritja ki a hivas adatait. Kulonben a kolléga pont azt nem latna,
        MELYIK hivashoz rendel ugyfelet.
      */
      className={`my-1 flex flex-wrap items-center gap-3 rounded-card border px-3.5 py-2 ${
        live ? 'border-accent/40 bg-accent-soft' : 'border-border-soft bg-surface-raised'
      }`}
    >
      <span
        aria-hidden="true"
        className={`grid size-8 shrink-0 place-items-center rounded-full ${
          missed
            ? 'bg-alert-soft text-alert'
            : live
              ? 'bg-accent text-accent-ink'
              : 'bg-surface-hover text-ink-muted'
        } ${leg?.state === 'ringing' ? 'animate-pulse' : ''}`}
      >
        <Icon name="telefon" size="sm" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm">
          {/* A cimjegyzekbeli nev elol, mogotte a szam - ha van nev. */}
          {contactName ? (
            <>
              <span className="font-semibold">{contactName}</span>{' '}
              <span className="tabular-nums text-ink-muted">
                ({formatPhoneNumber(callerNumber)})
              </span>
            </>
          ) : (
            <span className="font-semibold tabular-nums">{formatPhoneNumber(callerNumber)}</span>
          )}
          <span className="text-ink-muted">
            {' '}
            · {outbound ? 'Kimenő hívás' : 'Bejövő hívás'}
            {lineLabel ? ` · ${lineLabel}` : ''}
          </span>
        </p>
        <p className={`truncate text-xs ${missed ? 'text-alert' : 'text-ink-muted'}`}>{detail}</p>
      </div>

      {live && !amInside && (
        <button
          type="button"
          disabled={callBack.busy}
          // A hivas a kulon ablakban fut - a fo ablak nem lep be maga.
          onClick={() => void openHuddleWindow(message.channelId)}
          className="shrink-0 rounded-pill bg-accent px-3 py-1 text-xs font-semibold text-accent-ink transition-colors duration-(--duration-quick) hover:bg-accent-hover disabled:opacity-50"
        >
          {leg?.state === 'ringing' && !outbound ? 'Felveszem' : 'Csatlakozás'}
        </button>
      )}
      {live && amInside && <span className="shrink-0 text-2xs text-accent">bent vagy</span>}
      {/*
        A HIVAS UTAN lehet a szamot ugyfelhez kotni - itt mar tudjuk, ki volt.
        Csak akkor ajanljuk fel, ha a szamhoz meg nincs nev; a csengeto ablakban
        pedig egyaltalan nem (lasd CustomerCard: `assignable`).
      */}
      {/*
        Hozzarendelni csak azt kell, amit nem ismerunk. Ha a TT mar azonositotta
        a hivot (`ttClinicId`), a gomb csak zaj lenne - es felrevezetne, mintha
        a szam ismeretlen volna.
      */}
      {!live && callerNumber && !contactName && !call?.ttClinicId && !isMirror && (
        <AssignCustomer number={callerNumber} />
      )}
      {noteOpen && huddleId && (
        <TtCallNote huddleId={huddleId} number={callerNumber} onClose={() => setNoteOpen(false)} />
      )}
      {!live && huddleId && transcriptState && (
        <TranscriptButton huddleId={huddleId} transcriptState={transcriptState} />
      )}
      {notesCanvasId && <NotesButton channelId={message.channelId} canvasId={notesCanvasId} />}
      {/*
        JEGYZET A TT-BE - a hivas utan, a kolléga jovahagyasaval.

        Csak lezart hivasnal: kozben meg tart a beszelgetes, es az atirat sem
        teljes. A gomb akkor is ott van, ha nincs atirat - a jegyzetet kezzel
        is meg lehet irni.
      */}
      {!live && huddleId && !isMirror && (
        <button
          type="button"
          onClick={() => setNoteOpen((open) => !open)}
          className="shrink-0 rounded-pill border border-border px-2 py-0.5 text-2xs font-medium text-ink-muted transition-colors hover:text-ink"
        >
          <Icon name="jegyzet" size="sm" /> {customerSystem === 'tt' ? 'TT-jegyzet' : 'Jegyzet'}
        </button>
      )}
      {/* Visszahivas a szamra - akinek van joga kifele telefonalni. */}
      {!live && canCallOut && callerNumber && !isMirror && (
        <button
          type="button"
          disabled={callBack.busy}
          onClick={() => callBack.dial(callerNumber)}
          title={callBack.error ?? undefined}
          className="shrink-0 rounded-pill border border-border px-2 py-0.5 text-2xs font-medium text-ink-muted transition-colors hover:text-ink disabled:opacity-50"
        >
          <Icon name="telefon" size="sm" /> {outbound ? 'Újrahívás' : 'Visszahívás'}
        </button>
      )}

      <time className="shrink-0 text-2xs text-ink-faint tabular-nums" dateTime={start}>
        {formatTime(start)}
        {call && !missed ? `–${formatTime(call.endedAt)}` : ''}
      </time>
    </div>
  );
}

/**
 * NEM FOGADOTT HIVAS a beszelgetesben.
 *
 * Ket kulonbozo dolgot mond ugyanaz a sor, aszerint, hogy KI olvassa:
 *
 *  - akit kerestek, annak ez a lenyeg. Hatarozott, kiemelt kartyat kap,
 *    visszahivas-gombbal - eddig ugyanis semmi nem szolt neki: a hivas
 *    elmult, es legfeljebb egy halvany szurke sor maradt utana arrol, hogy
 *    valaki olelest inditott. Pont ez volt a panasz.
 *  - mindenki masnak ez a beszelgetes elozmenye: "itt hivas volt, nem vettek
 *    fel". Egy halk sor, ami nem kiabal ra olyasmiert, ami nem rola szol.
 *
 * Ugyanaz a minta, mint az emlitesnel: kozos uzenet, szemelyes jelentes.
 */
function MissedCallRow({ message, meId }: { message: LocalMessage; meId: string }) {
  const { users, nameOf } = useUsers();
  const callBack = useDialFromChannel(message.channelId, meId);
  const phoneContacts = usePhoneContacts();
  const canCallOut = users.find((user) => user.id === meId)?.canCallOut ?? false;

  const missed = Array.isArray(message.meta?.missedUserIds)
    ? (message.meta.missedUserIds as unknown[]).filter((id): id is string => typeof id === 'string')
    : [];
  const engem = missed.includes(meId);

  const callerId = typeof message.meta?.callerId === 'string' ? message.meta.callerId : null;
  // Telefonhivasnal a hivonak nincs felhasznaloja - a szama a jelzes.
  const callerNumber =
    typeof message.meta?.callerNumber === 'string' ? message.meta.callerNumber : null;
  const callerName = callerNumber
    ? phoneContacts.display(callerNumber)
    : nameOf(callerId ?? undefined, 'Valaki');
  const lineLabel = typeof message.meta?.lineLabel === 'string' ? message.meta.lineLabel : null;
  const task = taskFromMeta(message.meta?.task);
  /*
    KAPCSOLAT NELKUL elmaradt hivas (K2): a jelzes a kapcsolat visszaterte
    utan erkezik, de a hivas IDEJE a probalkozase - azt mutatjuk.
  */
  const offline = message.meta?.offline === true;
  const calledAt =
    typeof message.meta?.attemptedAt === 'string' ? message.meta.attemptedAt : message.createdAt;

  if (!engem) {
    /*
      A HIVO (es a tobbiek) oldalan is lathato kartya, nem halvany rendszersor:
      a "nem vette fel" is fontos - a halvany sor a tobbi kozott elveszett.
    */
    return (
      <p className="my-1 ml-3.5 flex w-fit max-w-full items-center gap-2 rounded-pill border border-alert/40 bg-alert-soft/60 px-3 py-1 text-xs text-ink-muted">
        <span aria-hidden="true" className="text-alert">
          <Icon name="kilepes" size="sm" />
        </span>
        {message.text}
        {missed.length > 0 && (
          <span className="ml-1 opacity-80">({missed.map((id) => nameOf(id)).join(', ')})</span>
        )}
        <time className="ml-1.5 text-2xs opacity-70" dateTime={calledAt}>
          {formatTime(calledAt)}
        </time>
      </p>
    );
  }

  return (
    <div className="mt-2 flex items-center gap-3 rounded-card border border-alert bg-alert-soft px-3.5 py-2.5 shadow-(--shadow-soft)">
      <span
        aria-hidden="true"
        className="grid size-9 shrink-0 place-items-center rounded-full bg-alert text-white"
      >
        <Icon name="kilepes" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="font-display text-sm font-semibold text-alert">Nem fogadott hívás</p>
        <p className="truncate text-xs text-ink-muted">
          {callerName} kereste{lineLabel ? ` · ${lineLabel}` : ''} ·{' '}
          <time dateTime={calledAt}>{formatTime(calledAt)}</time>
          {offline && ' · nem volt kapcsolata'}
        </p>
        {task && <TaskLink task={task} />}
      </div>

      {/*
        VISSZAHIVAS egy kattintasra.

        A jelzes akkor er valamit, ha a kovetkezo lepes is itt van: aki latja,
        hogy kerestek, az szinte mindig vissza akar hivni. Enelkul meg kellene
        keresnie a fejlecben az oleles-gombot.

        TELEFONHIVASNAL NINCS gomb: a "visszahivas" itt egy olelest inditana a
        csatornaban, nem a szamot hivna - amig nincs kimeno hivas (3. fazis),
        a szam a jelzes, a visszahivas a telefonon tortenik.
      */}
      {/*
        TELEFONHIVASNAL a visszahivas a SZAMOT hivja - de csak annak, akinek van
        joga kifele telefonalni (3. fazis). A tobbieknek a szam a jelzes.
      */}
      {callerNumber && canCallOut && (
        <button
          type="button"
          disabled={callBack.busy}
          onClick={() => callBack.dial(callerNumber)}
          title={callBack.error ?? undefined}
          className="shrink-0 rounded-pill bg-alert px-3 py-1.5 text-xs font-semibold text-white transition-opacity duration-(--duration-quick) hover:opacity-90 disabled:opacity-50"
        >
          <Icon name="telefon" size="sm" /> Visszahívás
        </button>
      )}
      {!callerNumber && (
        <button
          type="button"
          // A visszahivas is a hivas-ablakban indul.
          onClick={() => void openHuddleWindow(message.channelId)}
          className="shrink-0 rounded-pill bg-alert px-3 py-1.5 text-xs font-semibold text-white transition-opacity duration-(--duration-quick) hover:opacity-90 disabled:opacity-50"
        >
          <Icon name="huddle" size="sm" /> Visszahívás
        </button>
      )}
    </div>
  );
}

function SystemRow({ text, iso }: { text: string; iso: string }) {
  return (
    <p className="px-3.5 py-1.5 text-xs text-ink-faint">
      <span aria-hidden="true" className="mr-2 text-ink-faint/60">
        ·
      </span>
      {text}
      <time className="ml-1.5 text-2xs opacity-70" dateTime={iso}>
        {formatTime(iso)}
      </time>
    </p>
  );
}

/**
 * Naposzto.
 *
 * A {@link useMinute} nem dekoracio: a "Ma" es a "Tegnap" felirat a MOSTHOZ
 * kepest keszul. Egy ejszakan at nyitva hagyott ablakban a tegnapi
 * beszelgetes reggel is azt allitotta magarol, hogy "Ma" - telepitett
 * alkalmazasban pedig nincs is mivel ujratolteni.
 */
function DayDivider({ iso, onJump }: { iso: string; onJump: () => void }) {
  useMinute();

  return (
    <div className="my-4 flex items-center gap-3">
      <span className="h-px flex-1 bg-border-soft" />
      {/* A nap cimkeje egyben "Ugras datumra" gomb (U6) - ahogy a Slackben. */}
      <button
        type="button"
        onClick={onJump}
        title="Ugrás dátumra"
        className="rounded-pill border border-border-soft bg-surface px-3 py-0.5 text-2xs font-semibold uppercase tracking-wide text-ink-faint transition-colors duration-(--duration-quick) hover:border-border hover:text-ink"
      >
        {formatDay(iso)}
      </button>
      <span className="h-px flex-1 bg-border-soft" />
    </div>
  );
}

function UnreadDivider({ ref }: { ref?: React.Ref<HTMLDivElement> }) {
  return (
    <div ref={ref} className="mt-3 flex items-center gap-2.5">
      <span className="text-2xs font-semibold uppercase tracking-wide text-alert">Új üzenetek</span>
      <span className="h-px flex-1 bg-alert/40" />
    </div>
  );
}

/** Terero nelkul: nem "ures a csatorna", hanem "most nem latjuk" (K2). */
function OfflineNotice() {
  return (
    <div className="grid flex-1 place-items-center py-16">
      <div className="max-w-xs text-center">
        <p className="font-semibold">Nincs kapcsolat</p>
        <p className="mt-1 text-sm text-ink-muted">
          Az üzenetek most nem tölthetők be. Amit írsz, elküldjük, amint újra lesz kapcsolat.
        </p>
      </div>
    </div>
  );
}

function EmptyState({ isNotes }: { isNotes: boolean }) {
  /*
    A JEGYZETEKBEN mast kell mondani.

    Az altalanos szoveg azzal biztat, hogy "a tobbiek latni fogjak azonnal" -
    ami itt pont az ellenkezoje az igazsagnak. Egy ures oldal, ami rosszul
    mondja meg, mi tortenik a leirt szoveggel, rosszabb, mint a semmi.
  */
  return (
    <div className="grid flex-1 place-items-center py-16">
      <div className="max-w-xs text-center">
        <div aria-hidden="true" className="mb-3 text-3xl opacity-40">
          <Icon name={isNotes ? 'jegyzet' : 'szerkesztes'} />
        </div>
        <p className="font-semibold">{isNotes ? 'A te helyed' : 'Még nincs üzenet'}</p>
        <p className="mt-1 text-sm text-ink-muted">
          {isNotes
            ? 'Ide írhatsz magadnak: emlékeztetőt, linket, fájlt. Ezt senki más nem látja.'
            : 'Írd le az első gondolatot — a többiek látni fogják azonnal.'}
        </p>
      </div>
    </div>
  );
}
